import { ArrowDown, Lock, Search, User, UserCheck, UserPlus, Users } from 'react-feather';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { gql } from '__generated__';

import {
  JoinPolicy,
  TeamInput,
} from '__generated__/graphql';

import { ErrorBag } from '../helpers';
import { teamSchema } from './helpers';
import UI from '../UI';
import useQuery from '../../api/useQuery';
import useValidation from '../useValidation';

export enum TeamMode {
  Individual = 'individual',
  Create = 'create',
  Join = 'join',
}

export const GetTeamsForEventQuery = gql(`
  query GetTeamsForEvent($eventId: ID! $perPage: Int!, $teamTypeIds: [ID!],, $search: String) {
    event(id: $eventId) {
      id
      available_teams(per_page: $perPage, team_types: $teamTypeIds, search: $search) {
        total
        data {
          id
          title
        }
      }
    }
  }
`);

export const GetTeamByAccessCodeQuery = gql(`
  query GetTeamByAccessCode($eventId: ID!, $teamTypeId: ID!, $accessCode: String!) {
    event(id: $eventId) {
      id
      team_by_code(team_type_id: $teamTypeId, access_code: $accessCode) {
        id
        title
      }
    }
  }
`);

interface TeamType {
  id: string;
  join_policy: JoinPolicy;
}

export interface TeamPickerProps {
  team?: TeamInput | null;
  originalTeam?: TeamInput | null;
  eventId: string;
  teamType: TeamType;
  allowIndividuals?: boolean;
  allowCreateTeam?: boolean;
  allowJoinTeam?: boolean;
  onChange: (team: TeamInput | null, mode: TeamMode) => void;
  touched?: {
    id?: boolean;
    title?: boolean;
    access_code?: boolean;
  };
  touch?: (field: string) => void;
  untouch?: (field?: string) => void;
  errors?: ErrorBag;
  perPage?: number;
}

const MAX_LIMIT = 250;

const TeamPicker = ({
  team, originalTeam, eventId, teamType, allowIndividuals = true, allowCreateTeam = true, allowJoinTeam = true,
  onChange, touch, untouch, touched, errors: externalErrors, perPage = 5,
}: TeamPickerProps) => {
  const { t } = useTranslation('common');
  const nameRef = useRef(`team-${Math.round(Math.random() * 1000000)}`);

  const [search, setSearch] = useState('');
  const [accessCode, setAccessCode] = useState(team?.access_code);
  const [limit, setLimit] = useState(perPage);

  const defaultTeamMode = getDefaultTeamMode({
    allowIndividuals,
    allowCreateTeam,
    allowJoinTeam,
  });

  const [teamMode, setTeamMode] = useState(() => {
    if (team?.id) {
      return TeamMode.Join;
    }

    if (team?.title) {
      return TeamMode.Create;
    }

    return defaultTeamMode;
  });

  // Keep the team mode in sync when props change.
  useEffect(() => {
    if (teamMode === TeamMode.Join && !allowJoinTeam) {
      setTeamMode(defaultTeamMode);
    }

    if (teamMode === TeamMode.Create && !allowCreateTeam) {
      setTeamMode(defaultTeamMode);
    }
  }, [teamMode, allowJoinTeam, allowCreateTeam, defaultTeamMode]);

  const { data: teamsData, loading: loadingTeams } = useQuery(
    GetTeamsForEventQuery,
    {
      variables: {
        eventId,
        teamTypeIds: [teamType.id],
        search,
        perPage: limit,
      },
      skip: !(teamMode === 'join' && !team?.id) || teamType.join_policy !== JoinPolicy.public,
    },
  );

  const { data: privateTeamData, loading: loadingPrivateTeam } = useQuery(
    GetTeamByAccessCodeQuery,
    {
      variables: {
        eventId,
        teamTypeId: teamType.id,
        accessCode,
      },
      skip: !accessCode,
      onCompleted: ({ event: { team_by_code: team } }) => {
        if (team) {
          onChange({
            team_type: { id: teamType.id },
            id: team.id,
            title: team.title,
            access_code: accessCode,
          }, TeamMode.Join);
        }
      },
    },
  );

  const loading = loadingTeams || loadingPrivateTeam;

  const teams = teamsData
    ? teamsData.event.available_teams.data
    : (privateTeamData?.event.team_by_code && accessCode ? [privateTeamData?.event.team_by_code] : []);

  const totalTeams = teamsData?.event.available_teams.total || teams.length;

  const participateIndividually = () => {
    setTeamMode(TeamMode.Individual);
    onChange(null, TeamMode.Individual);
    untouch?.();
  };

  const createTeam = () => {
    setTeamMode(TeamMode.Create);
    // Explicitly set team title to empty string to mark it as required.
    onChange({ team_type: { id: teamType.id }, title: '' }, TeamMode.Create);
    untouch?.();
  };

  const joinTeam = (id?: string | null) => {
    setTeamMode(TeamMode.Join);
    setSearch('');
    setAccessCode('');
    setLimit(perPage);
    // Explicitly set team ID fallback to empty string to mark it as required.
    onChange({ team_type: { id: teamType.id }, id: id || '' }, TeamMode.Join);
    untouch?.();
  };

  const leaveTeam = () => {
    // Explicitly set team ID to empty string to mark it as required.
    onChange({ team_type: { id: teamType.id }, id: '' }, TeamMode.Join);
  };

  const handleSearch = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    setSearch(value);
    setLimit(perPage);
    // Explicitly set team ID to empty string to mark it as required.
    onChange({ team_type: { id: teamType.id }, id: '' }, teamMode);
  };

  const handleAccessCode = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    setAccessCode(value);
    setLimit(perPage);
    // Explicitly set team ID to empty string to mark it as required.
    onChange({ team_type: { id: teamType.id }, id: '' }, teamMode);
  };

  const { errors } = useValidation({
    schema: teamSchema,
    values: team || { team_type: { id: teamType.id } },
    externalErrors,
  });

  const handleTitleChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    // Explicitly set team title fallback to empty string to mark it as required.
    onChange({ team_type: { id: teamType.id }, title: value || '' }, teamMode);
  };

  const showRadioButtons = (allowIndividuals ? 1 : 0) + (allowCreateTeam ? 1 : 0)
    + (allowJoinTeam ? 1 : 0) > 1 || !!originalTeam?.id;

  const teamOptions = team?.id ? (
    <UI.Checkbox
      onChange={() => leaveTeam()}
      sc={{ box: true, block: true }}
      checked
    >
      <UI.FormGrid sc={{ columns: '14px 1fr', gutter: 0.25 }}>
        <UI.Div>
          <UI.Icon>
            <Users />
          </UI.Icon>
        </UI.Div>
        <UI.Div>
          <UI.Strong>
            {team.title}
          </UI.Strong>
        </UI.Div>
      </UI.FormGrid>
    </UI.Checkbox>
  ) : (
    <>
      {teams.length > 0 && (
        <UI.FormGrid sc={{ gutter: 0.25 }}>
          {teams.map((candidate) => (
            <UI.Checkbox
              onChange={() => onChange(
                {
                  team_type: { id: teamType.id },
                  id: candidate.id,
                  title: candidate.title,
                  access_code: accessCode || undefined,
                },
                teamMode,
              )}
              sc={{ box: true }}
              key={candidate.id}
            >
              <UI.FormGrid sc={{ columns: '14px 1fr', gutter: 0.25 }}>
                <UI.Div>
                  <UI.Icon>
                    <Users />
                  </UI.Icon>
                </UI.Div>
                <UI.Div>
                  <UI.Strong>
                    {candidate.title}
                  </UI.Strong>
                </UI.Div>
              </UI.FormGrid>
            </UI.Checkbox>
          ))}
        </UI.FormGrid>
      )}

      {!loading && totalTeams === 0 && (
        <>
          {teamType.join_policy === JoinPolicy.public && (
            search ? t('team_picker.no_teams_found') : (
              <UI.Span>
                {t('team_picker.no_teams_yet')}
                {allowCreateTeam && (
                  <>
                    {' '}
                    <UI.A role="button" onClick={() => createTeam()}>
                      {t('team_picker.create_new_team')}
                    </UI.A>
                  </>
                )}
              </UI.Span>
            )
          )}
          {teamType.join_policy === JoinPolicy.private && accessCode && t('team_picker.no_teams_found')}
        </>
      )}

      {totalTeams > limit && (
        <UI.Button
          onClick={() => setLimit((limit) => limit + perPage)}
          sc={{ borderRadius: 'md' }}
          disabled={loading || limit >= MAX_LIMIT}
        >
          <UI.Icon>
            <ArrowDown />
          </UI.Icon>
          {' '}
          {t('team_picker.show_more')}
        </UI.Button>
      )}
    </>
  );

  return (
    <UI.FormGrid>
      {showRadioButtons && (
        <UI.InputGroup sc={{ mb: teamMode === TeamMode.Individual ? 0 : undefined }}>
          <UI.FormGrid sc={{ gutter: 0.25 }}>
            {originalTeam && (
              <UI.Radio
                name={`${nameRef.current}-mode`}
                onChange={() => joinTeam(originalTeam.id)}
                checked={team?.id === originalTeam.id}
                sc={{ box: true }}
              >
                <UI.FormGrid sc={{ columns: '14px 1fr', gutter: 0.25 }}>
                  <UI.Div>
                    <UI.Icon>
                      <UserCheck />
                    </UI.Icon>
                  </UI.Div>
                  <UI.Div>
                    <UI.Strong sc={{ block: true }}>
                      {originalTeam.title}
                    </UI.Strong>
                    {t('team_picker.stay_in_current_team')}
                  </UI.Div>
                </UI.FormGrid>
              </UI.Radio>
            )}
            {allowIndividuals && (
              <UI.Radio
                name={`${nameRef.current}-mode`}
                onChange={() => participateIndividually()}
                checked={teamMode === TeamMode.Individual}
                sc={{ box: true }}
              >
                <UI.FormGrid sc={{ columns: '14px 1fr', gutter: 0.25 }}>
                  <UI.Div>
                    <UI.Icon>
                      <User />
                    </UI.Icon>
                  </UI.Div>
                  <UI.Div>
                    <UI.Strong sc={{ block: true }}>
                      {t('team_picker.participate_individually')}
                    </UI.Strong>
                    {t('team_picker.team_mode_individual_description')}
                  </UI.Div>
                </UI.FormGrid>
              </UI.Radio>
            )}
            {allowJoinTeam && (
              <UI.Radio
                name={`${nameRef.current}-mode`}
                onChange={() => joinTeam()}
                checked={teamMode === TeamMode.Join && (!originalTeam || originalTeam.id !== team?.id)}
                sc={{ box: true }}
              >
                <UI.FormGrid sc={{ columns: '14px 1fr', gutter: 0.25 }}>
                  <UI.Div>
                    <UI.Icon>
                      <UserPlus />
                    </UI.Icon>
                  </UI.Div>
                  <UI.Div>
                    <UI.Strong sc={{ block: true }}>
                      {t('team_picker.join_existing_team')}
                    </UI.Strong>
                    {t('team_picker.team_mode_join_description')}
                  </UI.Div>
                </UI.FormGrid>
              </UI.Radio>
            )}
            {allowCreateTeam && (
              <UI.Radio
                onChange={() => createTeam()}
                name={`${nameRef.current}-mode`}
                sc={{ box: true }}
                checked={teamMode === TeamMode.Create}
              >
                <UI.FormGrid sc={{ columns: '14px 1fr', gutter: 0.25 }}>
                  <UI.Div>
                    <UI.Icon>
                      <Users />
                    </UI.Icon>
                  </UI.Div>
                  <UI.Div>
                    <UI.Strong sc={{ block: true }}>
                      {t('team_picker.create_new_team')}
                    </UI.Strong>
                    {t('team_picker.team_mode_create_description')}
                  </UI.Div>
                </UI.FormGrid>
              </UI.Radio>
            )}
          </UI.FormGrid>
        </UI.InputGroup>
      )}

      {teamMode === TeamMode.Create && (
        <>
          <UI.InputGroup sc={{ valid: !errors?.title, touched: touched?.title, mb: 0 }}>
            <UI.InputLabel htmlFor="TeamTitle">
              {t('team_picker.team_name')}
              {' '}
              <UI.RequiredMark />
            </UI.InputLabel>
            <UI.DebouncedInput
              type="text"
              value={team?.title || ''}
              onChange={handleTitleChange}
              name={`${nameRef.current}-title`}
              onBlur={() => touch?.('title')}
              id="TeamTitle"
            />
            <UI.ErrorMessages attribute={t('team_picker.team_name')} errors={errors.title} />
          </UI.InputGroup>

          {teamType.join_policy === JoinPolicy.private && allowJoinTeam && (
            <UI.Info icon={<Lock />}>
              {t('team_picker.new_team_access_code_description')}
            </UI.Info>
          )}
        </>
      )}

      {teamMode === TeamMode.Join && (!team?.id || team.id !== originalTeam?.id) && (
        <UI.FormGrid sc={{ gutter: 0.5 }}>
          {teamType.join_policy === JoinPolicy.public && (
            <UI.InputGroup sc={{ valid: !errors.id, touched: touched?.id }}>
              <UI.InputLabel htmlFor="TeamSearch">
                {t('team_picker.team')}
                {' '}
                <UI.RequiredMark />
              </UI.InputLabel>
              <UI.InputWrapper>
                <UI.Span>
                  <UI.Icon>
                    {search && loading ? <UI.Loader /> : <Search />}
                  </UI.Icon>
                </UI.Span>
                <UI.DebouncedInput
                  value={search}
                  onChange={handleSearch}
                  name={`${nameRef.current}-search`}
                  sc={{ pl: 5 }}
                  id="TeamSearch"
                  placeholder={t('team_picker.find_your_team')}
                />
              </UI.InputWrapper>
            </UI.InputGroup>
          )}

          {teamType.join_policy === JoinPolicy.private && (
            <UI.InputGroup sc={{ valid: !errors.id, touched: touched?.id }}>
              <UI.InputLabel htmlFor="TeamAccessCode">
                {t('team_picker.access_code')}
                {' '}
                <UI.RequiredMark />
              </UI.InputLabel>
              <UI.InputDescription>
                {t('team_picker.enter_access_code_description')}
              </UI.InputDescription>
              <UI.InputWrapper>
                <UI.Span>
                  <UI.Icon>
                    {loadingPrivateTeam ? <UI.Loader /> : <Lock />}
                  </UI.Icon>
                </UI.Span>
                <UI.DebouncedInput
                  value={accessCode}
                  onChange={handleAccessCode}
                  name={`${nameRef.current}-access_code`}
                  onBlur={() => touch?.('access_code')}
                  sc={{ pl: 5 }}
                  id="TeamAccessCode"
                />
              </UI.InputWrapper>
            </UI.InputGroup>
          )}

          {teamOptions}
        </UI.FormGrid>
      )}
    </UI.FormGrid>
  );
};

const getDefaultTeamMode = ({
  allowIndividuals, allowJoinTeam, allowCreateTeam,
}: {
  allowIndividuals: boolean;
  allowJoinTeam: boolean;
  allowCreateTeam: boolean;
}): TeamMode | null => {
  if (allowIndividuals) {
    return TeamMode.Individual;
  }

  if (allowJoinTeam) {
    return TeamMode.Join;
  }

  if (allowCreateTeam) {
    return TeamMode.Create;
  }

  return null;
};

export default TeamPicker;
